// Lead Generation System — the finalized pay-period ledger. Read-only view of
// LOCKED weeks a manager reads then pays off-system: one generator's weekly
// history, an all-generators view for a selected past week, drill-in to a week's
// underlying earning rows, and CSV exports. DISPLAY-ONLY — no money moves in the
// system. Reads gate on LeadGen.View (route-gated; this guard is defense-in-depth).

import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { calendarDateInputToIso, formatCalendarDate } from "@/design-system/lib/format";
import { earningsApi } from "@/api/leadGen/earnings";
import { downloadBlob } from "@/api/reports";
import { useLeadGenerators } from "@/hooks/leadGen/useLeadGenerators";
import {
  useLedgerHistory,
  useLedgerWeek,
  useLedgerWeekLineItems,
} from "@/hooks/leadGen/useEarnings";
import { bucketLabel, EARNING_STATE_BADGE, KPI_LABEL, money, todayInputDate } from "./leadGenShared";
import type { LeadGenEarningState } from "@/api/leadGen/earnings";

function kpiLabel(code: string): string {
  if (code === "manual_adjustment") return "Manual adjustment";
  return KPI_LABEL[code] ?? code;
}

type Drill = { generatorId: string; weekOf: string; name: string };

export function PayPeriodLedgerPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const { date } = useDateFormatter();

  const generators = useLeadGenerators({ page: 1, pageSize: 200 });
  const generatorNames = useMemo(
    () => new Map((generators.data?.data ?? []).map((g) => [g.id, g.displayName])),
    [generators.data],
  );

  const [generatorId, setGeneratorId] = useState("");
  const [weekDate, setWeekDate] = useState(todayInputDate());
  const weekIso = useMemo(() => calendarDateInputToIso(weekDate) ?? "", [weekDate]);

  const [drill, setDrill] = useState<Drill | null>(null);
  const [exporting, setExporting] = useState(false);

  const history = useLedgerHistory(generatorId || null);
  const week = useLedgerWeek(weekIso);

  if (!hasPermission("LeadGen.View")) {
    return (
      <div className="flex flex-col gap-space-4">
        <PageHeader eyebrow="Lead Generation" title="Pay-period ledger" />
        <EmptyState
          title="Not authorized"
          description="You need LeadGen.View to see the lead-generation pay-period ledger."
        />
      </div>
    );
  }

  const exportHistory = async () => {
    if (!generatorId) return;
    setExporting(true);
    try {
      const blob = await earningsApi.exportHistoryCsv(generatorId);
      downloadBlob(blob, `lead-gen-history-${generatorId}.csv`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const exportWeek = async () => {
    if (!weekIso) return;
    setExporting(true);
    try {
      const blob = await earningsApi.exportWeekCsv(weekIso);
      downloadBlob(blob, `lead-gen-pay-week-${weekDate}.csv`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-space-5">
      <PageHeader
        eyebrow="Lead Generation"
        title="Pay-period ledger"
        description="Finalized (locked) pay weeks — the figures a manager reads and then pays off-system. DISPLAY-ONLY: no money moves in the system. Drill into a week to see the underlying KPI actions and adjustments; export any view to CSV."
      />

      {/* One generator's weekly history */}
      <Card>
        <div className="flex flex-wrap items-end justify-between gap-space-3 mb-space-3">
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-wide text-gray-mid">Generator history</span>
            <select
              className="h-10 w-64 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
              value={generatorId}
              onChange={(e) => setGeneratorId(e.target.value)}
            >
              <option value="">Select a generator…</option>
              {(generators.data?.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.displayName}
                </option>
              ))}
            </select>
          </label>
          <Button
            variant="secondary"
            size="sm"
            disabled={!generatorId || exporting || (history.data?.length ?? 0) === 0}
            onClick={exportHistory}
          >
            <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
          </Button>
        </div>

        {!generatorId ? (
          <EmptyState title="Pick a generator" description="Choose a generator to see their finalized weekly history." />
        ) : history.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : history.isError ? (
          <p className="text-small text-status-error" role="alert">
            {extractErrorMessage(history.error)}
          </p>
        ) : (history.data?.length ?? 0) === 0 ? (
          <EmptyState title="No locked weeks" description="This generator has no finalized (locked) pay weeks yet." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Week of</TH>
                <TH>Grade</TH>
                <TH className="text-right">Multiplier</TH>
                <TH className="text-right">Approved gross</TH>
                <TH className="text-right">Adjustments</TH>
                <TH className="text-right">Approved net</TH>
                <TH>Locked</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {(history.data ?? []).map((row) => (
                <TR key={row.payPeriodId}>
                  <TD className="font-semibold text-onyx">{formatCalendarDate(row.weekOf)}</TD>
                  <TD>{row.qualityGrade ? <Badge status="gold">{row.qualityGrade}</Badge> : "—"}</TD>
                  <TD className="text-right tabular-nums">
                    {row.qualityMultiplier != null ? `${row.qualityMultiplier.toFixed(2)}×` : "—"}
                  </TD>
                  <TD className="text-right tabular-nums">{money(row.approvedGross)}</TD>
                  <TD className="text-right tabular-nums text-gray-mid">{money(row.totalAdjustments)}</TD>
                  <TD className="text-right tabular-nums font-semibold text-onyx">{money(row.approvedNet)}</TD>
                  <TD className="text-caption text-gray-mid">{row.lockedAt ? date(row.lockedAt) : "—"}</TD>
                  <TD className="text-right">
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() =>
                        setDrill({
                          generatorId,
                          weekOf: row.weekOf,
                          name: generatorNames.get(generatorId) ?? "Generator",
                        })
                      }
                    >
                      Drill in
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* All generators for a selected past week */}
      <Card>
        <div className="flex flex-wrap items-end justify-between gap-space-3 mb-space-3">
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-wide text-gray-mid">Weekly pay run (all generators)</span>
            <Input
              type="date"
              value={weekDate}
              onChange={(e) => setWeekDate(e.target.value)}
              className="w-48"
            />
          </label>
          <Button
            variant="secondary"
            size="sm"
            disabled={!weekIso || exporting || (week.data?.length ?? 0) === 0}
            onClick={exportWeek}
          >
            <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
          </Button>
        </div>

        {week.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : week.isError ? (
          <p className="text-small text-status-error" role="alert">
            {extractErrorMessage(week.error)}
          </p>
        ) : (week.data?.length ?? 0) === 0 ? (
          <EmptyState
            title="No locked periods"
            description="No generator has a finalized (locked) pay period for the selected week."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Generator</TH>
                <TH>Grade</TH>
                <TH className="text-right">Multiplier</TH>
                <TH className="text-right">Approved gross</TH>
                <TH className="text-right">Adjustments</TH>
                <TH className="text-right">Approved net</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {(week.data ?? []).map((row) => (
                <TR key={row.payPeriodId}>
                  <TD className="font-semibold text-onyx">{row.leadGeneratorName}</TD>
                  <TD>{row.qualityGrade ? <Badge status="gold">{row.qualityGrade}</Badge> : "—"}</TD>
                  <TD className="text-right tabular-nums">
                    {row.qualityMultiplier != null ? `${row.qualityMultiplier.toFixed(2)}×` : "—"}
                  </TD>
                  <TD className="text-right tabular-nums">{money(row.approvedGross)}</TD>
                  <TD className="text-right tabular-nums text-gray-mid">{money(row.totalAdjustments)}</TD>
                  <TD className="text-right tabular-nums font-semibold text-onyx">{money(row.approvedNet)}</TD>
                  <TD className="text-right">
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() =>
                        setDrill({ generatorId: row.leadGeneratorId, weekOf: row.weekOf, name: row.leadGeneratorName })
                      }
                    >
                      Drill in
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {drill && <LineItemsCard drill={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

// --- Week drill-in (underlying earning rows) -------------------------------

function LineItemsCard({ drill, onClose }: { drill: Drill; onClose: () => void }) {
  const { dateTime } = useDateFormatter();
  const items = useLedgerWeekLineItems(drill.generatorId, drill.weekOf);

  return (
    <Card featured>
      <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-3">
        <div>
          <h3 className="font-display text-h3 text-onyx m-0">Week detail — {drill.name}</h3>
          <p className="text-caption text-gray-mid m-0">
            {formatCalendarDate(drill.weekOf)} week · each KPI action and manual adjustment that composed the figure.
          </p>
        </div>
        <Button variant="text" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      {items.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : items.isError ? (
        <p className="text-small text-status-error" role="alert">
          {extractErrorMessage(items.error)}
        </p>
      ) : (items.data?.length ?? 0) === 0 ? (
        <EmptyState title="No rows" description="No approved earning rows composed this pay week." />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Item</TH>
              <TH>Bucket</TH>
              <TH>State</TH>
              <TH className="text-right">Earned</TH>
              <TH className="text-right">Credited</TH>
              <TH>Approved</TH>
              <TH>Note</TH>
            </TR>
          </THead>
          <TBody>
            {(items.data ?? []).map((row) => (
              <TR key={row.id}>
                <TD className="font-semibold text-onyx">{kpiLabel(row.kpiCode)}</TD>
                <TD className="text-gray-mid">{row.isAdjustment ? "—" : bucketLabel(row.bucket)}</TD>
                <TD>
                  <Badge status={EARNING_STATE_BADGE[row.state as LeadGenEarningState]}>{row.state}</Badge>
                </TD>
                <TD className="text-right tabular-nums text-gray-mid">
                  {row.isAdjustment ? "—" : money(row.earnedAmount)}
                </TD>
                <TD className="text-right tabular-nums">{money(row.creditedAmount)}</TD>
                <TD className="text-caption text-gray-mid">{row.approvedAtUtc ? dateTime(row.approvedAtUtc) : "—"}</TD>
                <TD className="text-caption text-gray-mid">{row.adjustmentReason ?? row.capReason ?? ""}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
